"use client";

// useUserCountry — single hook every component uses to read + change the
// current user's country preference. Drives:
//   - sell page country toggle (which currency the user is selling for)
//   - dashboard locale formatting fallback (when no transactions yet)
//   - home page hero rate
//   - payment-methods default country
//   - phone verification example
//
// Resolution order:
//   1. Registered users (User.countryCode set) — read from /api/v1/auth/me
//   2. Anonymous (or registered without preference) — read smo_country cookie
//   3. None of the above — fall back to DEFAULT_COUNTRY
//
// setCountry persists to BOTH the cookie AND (if registered) /api/v1/auth/me.
// So a user who picks GH on the sell page sees GH everywhere on next load,
// across devices when registered.
//
// activeCountries comes from /api/v1/countries — the runtime intersection
// of the static registry and the DB Country.isActive flag. Components use
// it to render dynamic country pickers without re-fetching themselves.

import { useCallback, useEffect, useState } from "react";
import { COUNTRIES, type CountryConfig, isValidCountryCode } from "@/lib/countries";
import { DEFAULT_COUNTRY } from "@/lib/constants/limits";
import { fetchWithCsrf } from "@/lib/api/fetch-csrf";

const COOKIE_KEY = "smo_country";
const COOKIE_MAX_AGE_DAYS = 365;

function readCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_KEY}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(value: string) {
  if (typeof document === "undefined") return;
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; samesite=lax`;
}

export type UserCountryState = {
  /** Currently-selected country config (resolved from one of the sources above). */
  country: CountryConfig;
  /** All countries currently active in the DB AND registered in src/lib/countries.ts.
   *  null until the API responds; components can fall back to allRegisteredCountries
   *  if they need a synchronous list. */
  activeCountries: CountryConfig[] | null;
  /** Persist a new country selection. Writes the cookie immediately + (best
   *  effort) PATCHes /api/v1/auth/me for registered users. */
  setCountry: (code: string) => void;
  /** True until the initial /api/v1/auth/me lookup finishes — useful for
   *  hiding the toggle until we know the right default. */
  isLoading: boolean;
};

export function useUserCountry(): UserCountryState {
  const [code, setCode] = useState<string>(() => {
    // First synchronous read — cookie wins over DEFAULT for repeat visits.
    // The async /me call below may overwrite this once if the user is
    // registered AND has a different country saved.
    if (typeof window === "undefined") return DEFAULT_COUNTRY;
    const cookie = readCookie();
    return cookie && isValidCountryCode(cookie) ? cookie.toUpperCase() : DEFAULT_COUNTRY;
  });
  const [activeCountries, setActiveCountries] = useState<CountryConfig[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Hydrate from /me + /countries on mount.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch("/api/v1/auth/me", { cache: "no-store" }).then((r) => r.json()).catch(() => null),
      fetch("/api/v1/countries", { cache: "no-store" }).then((r) => r.json()).catch(() => null),
    ]).then(([me, list]) => {
      if (cancelled) return;
      // If the registered user has a saved preference, prefer it over the
      // cookie. Anonymous users keep whatever the cookie said.
      if (me?.countryCode && isValidCountryCode(me.countryCode)) {
        const upper = me.countryCode.toUpperCase();
        setCode(upper);
        // Sync cookie so cross-device read on next visit is correct
        writeCookie(upper);
      }
      if (Array.isArray(list?.countries)) {
        setActiveCountries(
          list.countries
            .map((c: { code: string }) => COUNTRIES[c.code])
            .filter((c: CountryConfig | undefined): c is CountryConfig => !!c),
        );
      }
      setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const setCountry = useCallback((next: string) => {
    if (!isValidCountryCode(next)) return;
    const upper = next.toUpperCase();
    setCode(upper);
    writeCookie(upper);
    // Best-effort persist for registered users. Anonymous: 401 silently
    // ignored — cookie is enough.
    fetchWithCsrf("/api/v1/auth/me", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ countryCode: upper }),
    }).catch(() => { /* non-fatal */ });
  }, []);

  // Resolve the config — should always succeed because `code` is gated
  // through isValidCountryCode at every write, but defensive in case the
  // registry shrank between writes (e.g. a country was removed).
  const country = COUNTRIES[code] ?? COUNTRIES[DEFAULT_COUNTRY];

  return { country, activeCountries, setCountry, isLoading };
}
